import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface KPITrendChartProps {
  data: {
    labels: string[]
    datasets: Array<{
      label: string
      data: number[]
      borderColor?: string
      backgroundColor?: string
    }>
  }
  title: string
  unit?: string
}

export default function KPITrendChart({ data, title, unit = '' }: KPITrendChartProps) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 12,
          },
          usePointStyle: true,
          padding: 15,
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          family: "'Product Sans', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 16,
          weight: 'bold' as const,
        },
        color: '#0b0b0b',
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(11, 11, 11, 0.9)',
        titleFont: {
          family: "'Product Sans', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 14,
        },
        bodyFont: {
          family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          size: 13,
        },
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }
            if (context.parsed.y !== null) {
              const value = context.parsed.y.toLocaleString()
              label += unit ? `${value} ${unit}` : value
            }
            return label
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 11,
          },
          callback: function (value: any) {
            if (typeof value === 'number') {
              return unit ? `${value.toLocaleString()} ${unit}` : value.toLocaleString()
            }
            return value
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        ticks: {
          font: {
            family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            size: 11,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  }

  const chartData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || (index === 0 ? '#2f577b' : '#b68a2f'),
      backgroundColor: dataset.backgroundColor || (index === 0 ? 'rgba(47, 87, 123, 0.1)' : 'rgba(182, 138, 47, 0.1)'),
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderWidth: 2,
      tension: 0.3,
      fill: true,
    })),
  }

  return (
    <div className="w-full h-[300px]">
      <Line options={options} data={chartData} />
    </div>
  )
}
